import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Loader2, Flame } from "lucide-react";
import { useCartStore, type ShopifyProduct } from "@/stores/cartStore";
import { toast } from "sonner";

interface ProductCardProps {
  product: ShopifyProduct;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  const { node } = product;
  const selectedVariant = node.variants.edges[0]?.node;
  const image = node.images.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedVariant) return;
    await addItem({
      product,
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity: 1,
      selectedOptions: selectedVariant.selectedOptions || [],
      productType: 'sticker',
    });
    toast.success("Ajouté au panier !", { position: "top-center" });
  };

  return (
    <Link
      to={`/product/${node.handle}`}
      className="group relative block rounded-2xl overflow-hidden border border-primary/15 bg-card hover:border-primary/30 hover:shadow-warm transition-all duration-300 hover:-translate-y-1"
    >
      {/* Low stock badge */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-1 bg-destructive text-destructive-foreground text-xs font-body font-bold px-2.5 py-1 rounded-full shadow-md animate-pulse">
        <Flame className="w-3 h-3" />
        Stock limité
      </div>
      {/* Cultural accent — maple leaf */}
      <div className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center text-sm shadow-sm border border-border transition-all duration-300 group-hover:scale-125 group-hover:rotate-12 group-hover:shadow-md">
        🍁
      </div>
      <div className="aspect-square overflow-hidden bg-secondary">
        {image ? (
          <img
            src={`${image.url}&width=612`}
            alt={image.altText || node.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            width="306"
            height="306"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ShoppingCart className="w-12 h-12" />
          </div>
        )}
      </div>
      <div className="p-4 space-y-3">
        <h3 className="font-display font-semibold text-lg truncate">{node.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 font-body">{node.description}</p>

        {/* Product type badge */}
        <div className="flex gap-1.5">
          <span className="py-1.5 px-2 rounded-lg text-xs font-body font-medium border border-primary bg-primary/10 text-primary shadow-sm">
            🏷️ Adhésif
          </span>
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-xl font-display font-bold text-primary">
            {price.currencyCode} {parseFloat(price.amount).toFixed(2)}
          </span>
          <Button
            size="sm"
            onClick={handleAddToCart}
            disabled={isLoading || !selectedVariant?.availableForSale}
            className="bg-primary text-primary-foreground hover:bg-terracotta-dark shadow-warm"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShoppingCart className="w-4 h-4 mr-1" />Ajouter</>}
          </Button>
        </div>
      </div>
    </Link>
  );
};
